import Tippy from '@tippyjs/react';
import React, { useState } from 'react';
import { Message, Role, User } from 'types';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar";
import Icon from '@/icons';
import { useQueryClient } from '@tanstack/react-query';
import { UserPopover } from '@/components/Popover/User';
import BotTag from '@/components/Tags/Bot';
import EmbedContent from './Embeds/Embed';
import AttachmentsDisplay from './Attachments/Display';
import { useParams } from 'react-router-dom';
//@ts-ignore
import Twemoji from 'react-twemoji';
import MessageInviteDisplay from './Links/Invites';
import ErrorBoundary from '@/ErrorBoundary';
import { MessageReport } from '@/components/Dialogs/Reports/Message';
import Markdown from '@/components/markdown/markdown';
import EmojiDropdown from '@/components/Dropdown/Emojis';
import Reactions from './Reactions/Reactions';
import { ManageMember } from '@/components/ContextMenu/Member';
import presences from '@/presences';
import TemplateDisplayMessage from './Links/Template';

type MessageExtended = Message & { state: string, failedToLoadReplies?: boolean, replyToMessage: Message };
const TextChannelMessage = React.memo(function Message({ message, user, permtodelete, replyTo, isDM, streamerMode }: {
    message: MessageExtended,
    user: User,
    permtodelete: boolean,
    replyTo: any,
    isDM: boolean,
    streamerMode: any
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedMessage, setEditedMessage] = useState(message.content);
    const [isDeleteStatus, setIsDeleteStatus] = useState(false);
    const [isEditingButton, setIsEditingButton] = useState(!message.content);
    const { teamId } = useParams();
    const queryClient = useQueryClient();
    const me = (queryClient.getQueryData(["team", teamId, "members", "@me"] as any) as any)?.member as any;
    const roles = (queryClient.getQueryData(["team", teamId, "roles"]) as any)?.roles as Role[]
    const members = (queryClient.getQueryState(["team", teamId, "members"]) as any);
    const emojis = (queryClient.getQueryState(["emojis"]) as any);
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") setIsEditing(false);
    });

    const extractLinks = (content: string, path: string) => {
        const siteurl = window.location.origin;
        const urlRegex = /(?:(?:https?:\/\/|www\.)[^\s/$.?#].[^\s]*)/g;
        const urls = [];
        let match;
        while ((match = urlRegex.exec(content)) !== null) {
            if (match[0].includes(`${siteurl}/${path}/`)) urls.push(match[0]);
        }
        return [...new Set(urls)];
    };

    const inviteLinks = (content: string) => extractLinks(content, "invite");
    const templateLinks = (content: string) => extractLinks(content, "template");

    const formatMessageContent = (content: string) => {
        const urlRegex = /(?:(?:https?:\/\/|www\.)[^\s/$.?#].[^\s]*)/g;
        return content?.replace(urlRegex, (url: string) => {
            try {
                new URL(url);
                if (streamerMode.enabled && streamerMode.details.hideInviteLinks && url.includes("/invite/") && url.includes(window.location.origin)) {
                    return "[Invite Link Hidden](" + url + ")";
                }
                return `[${url}](${url})`;
            } catch {
                return url;
            }
        });
    };


    const formatDate = (date: Date) => {
        const now = new Date();
        const messageDate = new Date(date);

        if (messageDate.toDateString() === now.toDateString())
            return `Today at ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);

        if (messageDate.toDateString() === yesterday.toDateString())
            return `Yesterday at ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

        return messageDate.toLocaleString();
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditedMessage(event.target.value);
        const trimmedValue = event.target.value.trim();
        setIsEditingButton(trimmedValue.length === 0 || trimmedValue.length > 2000 || Boolean(trimmedValue.match(/^#+\s*$/)) || false);
    };

    const handleDeleteClick = async () => {
        setIsDeleteStatus(true);
        await fetch(`__SITE_BASE__/api/v1/channels/${message.channelId}/messages/${message.id}`, { method: "DELETE" });
    };
    const handleSaveClick = async () => {
        if (message.content === editedMessage) return setIsEditing(false);
        const response = await fetch(`__SITE_BASE__/api/v1/channels/${message.channelId}/messages/${message.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content: editedMessage }),
        });

        if (response.ok) setIsEditing(false);
    };

    const userHighRole = roles?.reduce((acc: any) => {
        let userM = members?.data?.members?.find((member: any) => member.id === message.createdBy.id);
        userM = userM?.roles.map((roleId: string) => roles.find((role: any) => role.id === roleId && role.isDisplayedSeparately)?.priority)
            .filter((priority: number | undefined) => priority !== undefined);
        if (!userM) return roles.find((role) => role.id == teamId)?.priority;
        return Math.min(acc, Math.max(...userM));
    }, 0);

    const higherRoleColor = roles?.find((role) => role.priority === Number(userHighRole))?.color;
    const higherRoleColor2 = roles?.find((role) => role.priority === Number(userHighRole))?.color2;
    const repliedUserHighRole = roles?.reduce((acc: any) => {
        let userM = members?.data?.members?.find((member: any) => member.id === message?.replyToMessage?.createdBy?.id);
        userM = userM?.roles.map((role: any) => roles.find((r: any) => r.id === role)?.priority);
        if (!userM) return roles.find((role) => role.id == teamId)?.priority;
        return Math.min(acc, Math.max(...userM));
    }, 0);

    const repliedHigherRoleColor = roles?.find((role) => role.priority === Number(repliedUserHighRole))?.color;
    const repliedHigherRoleColor2 = roles?.find((role) => role.priority === Number(repliedUserHighRole))?.color2;

    const selectReaction = (emoji: any) => {
        fetch(`__SITE_BASE__/api/v1/channels/${message.channelId}/messages/${message.id}/reactions/${emoji.id || emoji.emoji}`, {
            method: "POST",
        });
    }

    const userData = (queryClient.getQueryData(["user"] as any) as any) as any;

    const unblockUser = async () => {
        await fetch(`__SITE_BASE__/api/me/users/${message.createdBy.id}/block`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        });
        queryClient.setQueryData(["user"], (old: any) => {
            const updatedBlocked = old.blocked.filter((block: any) => block.userId !== message.createdBy.id);
            return { ...old, blocked: updatedBlocked };
        });
    }

    if (userData?.blocked?.find((block: any) => block.userId == message.createdBy?.id)) return (
        <div className='px-2'>
            <div className='bg-brand-back text-red-400 rounded-lg p-4 flex gap-2 items-center'>
                <Icon name='shield' size='24' />
                <p className='text-gray-400'>This message is hidden because you have blocked this user.</p>

                <button className='px-2 py-1 rounded-lg flex gap-2 bg-brand-green text-white' onClick={unblockUser}>
                    <p>
                        Unblock <strong>{message.createdBy.username}</strong>
                    </p>
                </button>
            </div>
        </div>
    )

    return (
        <div className='px-2'>

            <div key={message.id} className={`relative group rounded-lg ${message?.replyToMessage?.createdBy?.id === user?.id ? 'bg-yellow-400/10 border-l-2 hover:bg-yellow-400/20 transition-all border-l-yellow-300/10 py-1' : ''} ${message?.content?.includes(`<@${me?.id || user?.id}>`) ? 'bg-yellow-400/10 border-l-2 hover:bg-yellow-400/20 transition-all border-l-yellow-300/10 py-1' : ''}`}>
                {message?.replyTo && (message?.replyToMessage || message.failedToLoadReplies) && (
                    <div className="flex items-center gap-2 rounded-lg px-5 relative">
                        <div className="flex absolute mt-4 p-2 py-4 ml-1 rounded-t-lg border-l-2 border-gray-700 pl-4 text-gray-400 group-hover:border-white transition-all duration-300" />
                        <div className="flex absolute mt-3 py-3 ml-1 rounded-l-lg w-8 border-t-2 border-gray-700 pl-4 text-gray-400 group-hover:border-white transition-all duration-300" />
                        <div className="flex items-center gap-2 px-4 rounded-lg">
                            <div className="flex items-center justify-center gap-2 w-4" />
                            {message.failedToLoadReplies ? (

                                <div className="flex items-center ml-2 w-full">
                                    <span className="font-semibold text-gray-400 text-base">Failed to load reply</span>
                                </div>
                            ) : (
                                <>
                                    <ErrorBoundary>
                                        <span className="font-light flex gap-2 text-gray-300 cursor-pointer">
                                            <ManageMember member={{
                                                ...message.replyToMessage.createdBy,
                                                roles: typeof members?.data?.members?.find((member: any) => member.id === message.replyToMessage.createdBy.id)?.roles !== "undefined" ? members.data?.members?.find((member: any) => member.id === message.replyToMessage.createdBy.id)?.roles : ["leave"],
                                            }}>
                                                <UserPopover user={{
                                                    ...message.replyToMessage.createdBy,
                                                    roles: typeof members?.data?.members?.find((member: any) => member.id === message.replyToMessage.createdBy.id)?.roles !== "undefined" ? members.data?.members?.find((member: any) => member.id === message.replyToMessage.createdBy.id)?.roles : ["leave"],
                                                }}>
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-semibold text-gray-300 bg-gray-600 rounded-full w-full">
                                                            <Avatar className='h-6 w-6'>
                                                                <AvatarImage src={message.replyToMessage.createdBy?.profilePicture?.replace("webp", "png")} className="rounded-full" onMouseOver={(e: any) => e.currentTarget.src = message.replyToMessage.createdBy?.profilePicture} onMouseOut={(e: any) => e.currentTarget.src = message.replyToMessage.createdBy?.profilePicture} />
                                                                <AvatarFallback>{message.replyToMessage.createdBy.username[0]}</AvatarFallback>
                                                            </Avatar>
                                                        </div>
                                                        <div className="font-semibold relative cursor-pointer text-xs" style={{
                                                            color: repliedHigherRoleColor
                                                        }}>
                                                            {message.replyToMessage.createdBy.username}
                                                            {repliedHigherRoleColor2 && (
                                                                <span className='GradientRole-ColorFix' style={{
                                                                    color: repliedHigherRoleColor2,
                                                                }}>
                                                                    {message.replyToMessage.createdBy.username}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </UserPopover>
                                            </ManageMember>
                                        </span>
                                        <div className="flex gap-2">
                                            <p className='font-light text-xs text-gray-300 truncate max-w-48 sm:max-w-[50rem] md:max-w-[50rem] lg:max-w-[50rem] xl:max-w-[50rem]'>
                                                {message.replyToMessage.content}
                                            </p>

                                            {message.replyToMessage.attachments.length != 0 && (
                                                <div className="flex items-center gap-2">
                                                    <Icon name="picture" size="16" color="gray" />
                                                </div>
                                            )}
                                        </div>
                                    </ErrorBoundary>
                                </>
                            )}
                        </div>
                    </div>
                )}



                <div className={`group flex gap-4 px-4 py-2 relative items-start rounded-lg justify-between ${message.replyTo || message?.content?.includes(`<@${me?.id || user?.id}>`) ? "" : "hover:bg-green-700/15"} transition-all overflow-x-hidden`} data-message-id={message.id} data-created-by={message.createdBy.id}>
                    <div className="flex items-center justify-center w-10 rounded-full cursor-pointer">
                        <ManageMember member={{
                            ...message?.createdBy,
                            roles: typeof members?.data?.members?.find((member: any) => member.id === message.createdBy?.id)?.roles !== "undefined" ? members?.data?.members?.find((member: any) => member.id === message?.createdBy?.id)?.roles : ["leave"],
                        }}>
                            <UserPopover user={{
                                ...message?.createdBy,
                                roles: typeof members?.data?.members?.find((member: any) => member.id === message.createdBy?.id)?.roles !== "undefined" ? members?.data?.members?.find((member: any) => member.id === message?.createdBy?.id)?.roles : ["leave"],
                            }}
                            >

                                {message.createdBy.profilePicture && (
                                    <div className="rounded-full relative flex items-center justify-center h-9 bg-brand-dark w-9">
                                        <img
                                            src={message.createdBy.profilePicture.replace("webp", "png")}
                                            className="rounded-full group-hover:hidden"
                                        />

                                        <img
                                            src={message.createdBy.profilePicture}
                                            className="rounded-full hidden group-hover:block"
                                        />

                                        <div className='absolute bottom-0 right-0 w-3 h-3 rounded-full' style={{ backgroundColor: presences.get(message.createdBy.presence) }} />
                                    </div>
                                ) || (
                                        <div className="rounded-full flex items-center justify-center h-9 bg-brand-dark w-9">
                                            <p className="text-xs">{message.createdBy.username[0]}</p>
                                        </div>
                                    )}
                            </UserPopover>
                        </ManageMember>
                    </div>
                    <div className="flex flex-col w-full overflow-hidden">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <div className='flex items-center'>
                                    <ManageMember member={{
                                        ...message.createdBy,
                                        roles: typeof members?.data?.members?.find((member: any) => member.id === message.createdBy?.id)?.roles !== "undefined" ? members?.data?.members?.find((member: any) => member.id === message?.createdBy?.id)?.roles : ["leave"],
                                    }}>
                                        <UserPopover user={{
                                            ...message.createdBy,
                                            roles: typeof members?.data?.members?.find((member: any) => member.id === message.createdBy?.id)?.roles !== "undefined" ? members?.data?.members?.find((member: any) => member.id === message?.createdBy?.id)?.roles : ["leave"],
                                        }}>
                                            <div className="flex items-center gap-2">
                                                <div className="font-semibold relative cursor-pointer text-sm" style={{
                                                    color: higherRoleColor
                                                }}>
                                                    {message.createdBy.username}
                                                    {higherRoleColor2 && (
                                                        <span className='GradientRole-ColorFix' style={{
                                                            color: higherRoleColor2,
                                                        }}>
                                                            {message.createdBy.username}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </UserPopover>
                                    </ManageMember>
                                    <BotTag user={message.createdBy} />
                                </div>
                                <Tippy content={new Date(message.createdAt).toLocaleString()}>
                                    <h1 className="text-gray-400 font-normal text-xs">{formatDate(message.createdAt)}</h1>
                                </Tippy>
                            </div>
                            <ErrorBoundary>
                                <div className={`flex items-start ${isEditing ? "gap-2" : "gap-1"} relative rounded-lg ${message.state == "sending" ? "text-gray-400" : ""} ${message.state == "failed" ? "text-red-400" : ""}`}>
                                    {isEditing ? (
                                        <div className="flex items-center w-full rounded-r-lg">
                                            <textarea
                                                style={{ resize: "none", scrollbarWidth: "none" }}
                                                value={editedMessage}
                                                onChange={handleInputChange}
                                                className="w-full h-12 px-2 py-4 rounded-l-lg bg-brand-profile text-gray-300 focus:outline-none resize-none leading-none overflow-auto"
                                                rows={1}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSaveClick();
                                                    }
                                                }}
                                            />
                                            <div className="bg-brand-profile flex items-center justify-center h-12 rounded-r-lg">
                                                <button className="flex items-center justify-center mr-2 px-2 h-8 w-8 rounded-full bg-brand-green transition-all duration-300 disabled:bg-brand-dark disabled:cursor-not-allowed" onClick={handleSaveClick} disabled={isEditingButton}>
                                                    <Icon name="send" color="white" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='flex flex-col justify-between'>
                                                <div className="overflow-hidden">
                                                    <Markdown message={message}>
                                                        {formatMessageContent(message.content)}
                                                    </Markdown>
                                                </div>
                                                <div className='space-y-2 items-start sm:max-w-max max-w-80'>
                                                    {message.embeds && message.embeds.map((embed, index) => (
                                                        <EmbedContent embed={embed} key={index} />
                                                    ))}
                                                    {message.attachments && (
                                                        <>
                                                            <AttachmentsDisplay attachments={message.attachments} />
                                                        </>
                                                    )}

                                                    {inviteLinks(message.content) && (
                                                        <MessageInviteDisplay links={inviteLinks(message.content).map((url) => ({ name: url, url })).slice(0, 5)} createdBy={message.createdBy} />
                                                    )}

                                                    {templateLinks(message.content) && (
                                                        <TemplateDisplayMessage links={templateLinks(message.content).map((url) => ({ name: url, url })).slice(0, 5)} createdBy={message.createdBy} />
                                                    )}

                                                    <Reactions message={message} selectReaction={selectReaction} />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {message.editedAt && !isEditing && (
                                        <Tippy content={new Date(message.editedAt).toLocaleString()} className="flex">
                                            <div className="flex align- gap-2 items-end justify-end ml-2">
                                                <p className="text-gray-400 text-xs">(edited)</p>
                                            </div>
                                        </Tippy>
                                    )}
                                    {message.state == "failed" && (
                                        <Tippy content="Message failed to send" className="flex">
                                            <div className="flex items-center justify-center w-6 p-2 h-6 bg-red-500 bg-opacity-50 rounded-full">
                                                <Icon name="x" size={"18"} color="white" />
                                            </div>
                                        </Tippy>
                                    )}
                                </div>
                            </ErrorBoundary>

                        </div>
                    </div>
                    
                </div>
                <div className="bg-brand-default absolute flex mr-2 mt-2 right-0 top-0 items-center justify-center rounded-lg w-0 overflow-hidden group-hover:w-auto">
                        <EmojiDropdown emojis={emojis.data} func={selectReaction}>
                            <button className="p-2 items-center hover:bg-brand-default rounded-lg transition-all duration-100">
                                <div className="text-gray-400 hover:text-white">
                                    <Icon name="emoji" size="18" />
                                </div>
                            </button>
                        </EmojiDropdown>

                        {
                            ((permtodelete) && (!message.state || message.state == "sent")) && (
                                <>
                                    {isEditing ? (
                                        <button className="p-2 items-center hover:bg-brand-default rounded-lg transition-all duration-100" onClick={handleSaveClick}>
                                            <div className="text-gray-400 hover:text-white">
                                                <Icon name="send" size="18" />
                                            </div>
                                        </button>
                                    ) : (
                                        message.createdBy.id === user.id && (
                                            <button className="p-2 items-center rounded-full hover:bg-brand-dark transition-all duration-100" onClick={handleEditClick}>
                                                <div className="text-gray-400 hover:text-white">
                                                    <Icon name="pencil" size="18" />
                                                </div>
                                            </button>
                                        )
                                    )}
                                    <button className="p-2 items-center rounded-full hover:bg-brand-dark transition-all duration-100 disabled:bg-brand-dark disabled:cursor-wait" onClick={handleDeleteClick} disabled={isDeleteStatus}>
                                        <div className="text-red-500 hover:text-red-600">
                                            <Icon name="delete" size="18" />
                                        </div>
                                    </button>
                                </>
                            )
                        }
                        {(!message.state || message.state == "sent") && (
                            <button className="p-2 items-center rounded-full hover:bg-brand-dark transition-all duration-100" onClick={() => replyTo(message)}>
                                <div className="text-gray-400 hover:text-white">
                                    <Icon name="reply" size="18" />
                                </div>
                            </button>
                        )}

                        {message.createdBy.id !== user.id && (
                            <MessageReport reportMessage={message}>
                                <button className="p-2 items-center rounded-full hover:bg-brand-dark transition-all duration-100">
                                    <div className="text-red-500 hover:text-red-600">
                                        <Icon name="shield" size="18" />
                                    </div>
                                </button>
                            </MessageReport>
                        )}


                    </div>

            </div>
        </div>
    );
});
export default TextChannelMessage;